import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BarcodeOutlined } from '@ant-design/icons';
import { Drawer, Typography, message } from 'antd';
import { CatalogItem, getCatalogItem } from '../../api/productCatalog';
import { getWarehouseLiquidCard, WarehouseLiquidCard } from '../../api/warehouse';
import useIsMobile from '../../hooks/useIsMobile';
import InfoSection from './InfoSection';
import MetaSection from './MetaSection';
import StocksSection from './StocksSection';
import OneCLinksSection from './OneCLinksSection';
import NationalCatalogSection from './NationalCatalogSection';

const { Text } = Typography;

type SectionKey = 'info' | 'stocks' | 'national' | 'meta' | 'onec';

const LAYOUTS: Record<'catalog' | 'warehouse', SectionKey[]> = {
  catalog: ['info', 'stocks', 'national', 'meta', 'onec'],
  warehouse: ['stocks', 'info', 'national', 'meta', 'onec'],
};

interface ProductCardDrawerProps {
  open: boolean;
  onClose: () => void;
  context: 'catalog' | 'warehouse';
  catalogId?: number | null;
  itemId?: string;
  extra?: (item: CatalogItem | null) => React.ReactNode;
  onItemUpdated?: (item: CatalogItem) => void;
}

// Rev. 17: базовая ширина — min(560, 45vw); если содержимое шире — карточка
// дооткрывается до него (потолок 760px, но не ниже базовой; порог 16px —
// защита от джиттера скроллбара/округления).
const drawerBaseWidth = () => Math.min(560, Math.round(window.innerWidth * 0.45));

const ProductCardDrawer: React.FC<ProductCardDrawerProps> = ({
  open,
  onClose,
  context,
  catalogId,
  itemId,
  extra,
  onItemUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState<CatalogItem | null>(null);
  const [card, setCard] = useState<WarehouseLiquidCard | null>(null);
  const [drawerWidth, setDrawerWidth] = useState(drawerBaseWidth);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open || (catalogId == null && !itemId)) return;
    setLoading(true);
    setItem(null);
    setCard(null);
    setDrawerWidth(drawerBaseWidth());
    const tasks: Promise<void>[] = [];
    if (catalogId != null) {
      tasks.push(
        getCatalogItem(catalogId)
          .then(setItem)
          .catch(() => { message.error('Ошибка загрузки карточки каталога'); }),
      );
    }
    tasks.push(
      getWarehouseLiquidCard(catalogId != null ? { catalog_id: catalogId } : { item_id: itemId! })
        .then(setCard)
        .catch(() => {
          // 404 — у карточки нет позиций 1С / её нет в снимке: секции склада скрыты.
          if (context === 'warehouse') {
            message.error('Не удалось загрузить карточку товара');
          }
        }),
    );
    Promise.all(tasks).finally(() => setLoading(false));
  }, [open, catalogId, itemId, context]);

  useLayoutEffect(() => {
    if (isMobile || loading || !contentRef.current) return;
    const base = drawerBaseWidth();
    const el = contentRef.current;
    const table = el.querySelector('.ant-table-content') as HTMLElement | null;
    const natural = table ? table.scrollWidth : el.scrollWidth;
    const measured = natural + 48;
    if (measured > base + 16) {
      setDrawerWidth(Math.min(measured, Math.max(base, 760)));
    }
  }, [isMobile, loading, item, card]);

  const handleItemUpdated = (updated: CatalogItem) => {
    setItem(updated);
    onItemUpdated?.(updated);
  };

  const title = item
    ? `${item.brand} — ${item.model_name}`
    : card?.display_name ?? 'Товар';

  const sections = LAYOUTS[context].map(key => {
    switch (key) {
      case 'info':
        return item ? <InfoSection key="info" item={item} onUpdated={handleItemUpdated} /> : null;
      case 'stocks':
        return card ? <StocksSection key="stocks" card={card} /> : null;
      case 'national':
        return item ? <NationalCatalogSection key="national" item={item} /> : null;
      case 'meta':
        return item ? <MetaSection key="meta" item={item} /> : null;
      case 'onec':
        return card && card.onec_items.length > 0
          ? <OneCLinksSection key="onec" card={card} />
          : item?.onec_items?.length
            ? <OneCLinksSection key="onec" items={item.onec_items} catalogId={item.id} />
            : null;
      default:
        return null;
    }
  });

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : drawerWidth}
      rootClassName="warehouse-card-drawer"
      loading={loading}
      extra={extra?.(item)}
    >
      {!loading && (item || card) && (
        <div ref={contentRef} style={{ display: 'inline-block', minWidth: '100%' }}>
          {card?.is_marked && (
            <div style={{ marginBottom: 12 }}>
              <BarcodeOutlined style={{ color: '#1677ff', marginRight: 6 }} />
              <Text style={{ color: '#1677ff' }}>Маркируемый товар</Text>
            </div>
          )}
          {sections}
        </div>
      )}
    </Drawer>
  );
};

export default ProductCardDrawer;
